import { translateDay } from 'helpers/translateDays';
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { Clock } from 'react-feather';

const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function getCurrentDayKey() {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[new Date().getDay()];
}

export default function HoursList({ data }) {
  const currentDay = getCurrentDayKey();

  return (
    <Container>
      <Header>
        <IconCircle>
          <Clock size={18} color={colors.neutralLight} />
        </IconCircle>
        <HeaderTitle>Horários de Funcionamento</HeaderTitle>
      </Header>

      <ScheduleList>
        {data &&
          dayOrder.map((day, i) => {
            const hours = data[day];
            if (hours === undefined) return null;
            const isToday = day === currentDay;
            const isClosed = !hours || hours.length === 0;
            const is24h = hours && hours.some((h) => h['24h']);

            return (
              <DayRow key={day} isToday={isToday} index={i}>
                <DayLeft>
                  <StatusDot isClosed={isClosed} isToday={isToday} />
                  <DayName isToday={isToday}>
                    {translateDay(day)}
                  </DayName>
                  {isToday && <TodayBadge>Hoje</TodayBadge>}
                </DayLeft>

                <DayRight>
                  {isClosed && <ClosedChip>Fechado</ClosedChip>}

                  {!isClosed && !is24h &&
                    hours.map((hour, idx) => (
                      <TimeChip key={idx} isToday={isToday}>
                        {hour.start}{hour.end ? ` \u2014 ${hour.end}` : ''}
                      </TimeChip>
                    ))}

                  {is24h && <AllDayChip>24 horas</AllDayChip>}
                </DayRight>
              </DayRow>
            );
          })}
      </ScheduleList>
    </Container>
  );
}

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled(Column)`
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const IconCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, ${colors.primaryLight}, ${colors.primary});
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 3px 10px rgba(79, 134, 247, 0.25);
`;

const HeaderTitle = styled.h3`
  font-size: ${fonts.sizes.highlight};
  font-weight: ${fonts.weight.bold};
  color: ${colors.nightDark};
  margin: 0;
  line-height: 1;
`;

const ScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DayRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 14px;
  border-radius: 10px;
  transition: background 0.2s ease;
  animation: ${fadeSlideIn} 0.35s ease forwards;
  animation-delay: ${({ index }) => index * 0.045}s;
  opacity: 0;

  ${({ isToday }) => isToday && `
    background: rgba(79, 134, 247, 0.06);
    box-shadow: inset 3px 0 0 0 rgba(79, 134, 247, 0.7);
  `}
`;

const DayLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ isClosed }) => isClosed ? colors.nightLight : colors.success};
  box-shadow: ${({ isClosed, isToday }) =>
    !isClosed && isToday
      ? `0 0 0 3px rgba(43, 200, 102, 0.15)`
      : 'none'};
  transition: box-shadow 0.3s ease;
`;

const DayName = styled.span`
  font-size: ${fonts.sizes.default};
  font-weight: ${({ isToday }) => isToday ? fonts.weight.bold : fonts.weight.regular};
  color: ${({ isToday }) => isToday ? colors.nightDark : colors.night};
  transition: color 0.2s ease;
`;

const TodayBadge = styled.span`
  font-size: 9px;
  font-weight: ${fonts.weight.bold};
  color: ${colors.primaryLight};
  background: rgba(79, 134, 247, 0.1);
  padding: 2px 7px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DayRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const TimeChip = styled.span`
  font-size: ${fonts.sizes.small};
  color: ${({ isToday }) => isToday ? colors.nightDark : colors.night};
  font-weight: ${fonts.weight.regular};
  padding: 4px 10px;
  border-radius: 6px;
  background: ${({ isToday }) => isToday
    ? 'rgba(79, 134, 247, 0.08)'
    : 'rgba(0, 0, 0, 0.035)'};
  white-space: nowrap;
  letter-spacing: 0.2px;
`;

const ClosedChip = styled.span`
  font-size: ${fonts.sizes.small};
  color: ${colors.danger};
  font-weight: ${fonts.weight.bold};
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(231, 80, 90, 0.07);
`;

const AllDayChip = styled.span`
  font-size: ${fonts.sizes.small};
  color: ${colors.successDark};
  font-weight: ${fonts.weight.bold};
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(43, 200, 102, 0.08);
`;
